<template>
  <div class="common-card" :key="routeKey">
    <modal-filter
      :filter_user_id.sync="filter_user_id"
      :filter_project_id.sync="filter_project_id"
      :filter_approval_id.sync="filter_approval_id"
      :filter_prepayment_status_id.sync="filter_prepayment_status_id"
      :is_user_page="is_user_page"
      :projectOptions="projectOptions"
      :userOptions="userOptions"
      :prepaymentStatusOptions="prepaymentStatusOptions"
      @refetch-data="refetchData"
    />
    <modal-status
      :prepayment-data="prepaymentData"
      :prepaymentStatusOptions="prepaymentStatusOptions"
      @refetch-data="refetchData"
    />
    <modal-approval :approve-data="approveData" @refetch-data="refetchData" />
    <b-card class="top mb-0">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex mb-1 mb-sm-0 align-items-center">
          <h4 class="font-weight-bolder ml-1 text-nowrap mb-0">
            <font-awesome-icon class="mr-50" icon="fa-solid fa-user-clock" />
            預支記錄
          </h4>
          <div class="d-flex ml-3 align-items-center">
            <label>顯示</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>條目</label>
          </div>
        </div>
        <div class="d-flex flex-wrap mobile-w100 mt-1 mt-lg-0">
          <b-form-input
            v-model="searchQuery"
            class="search-form mb-1 mb-sm-0 mr-sm-1"
            placeholder="搜尋記錄"
          />
          <b-button
            variant="primary"
            class="mobile-w100 mb-1 mb-sm-0 mr-sm-1"
            @click="$bvModal.show('modal-filter')"
            ><feather-icon
              class="mr-1"
              icon="filterIcon"
              size="18"
            ></feather-icon
            >篩選</b-button
          >
          <!-- 匯出功能按鈕 -->
          <b-dropdown
            v-if="ability.can('export', 'user_prepayments') && is_user_page || ability.can('export', 'prepayments') && !is_user_page"
            variant="success"
            text="匯出"
            class="mobile-w100 mb-1 mb-sm-0 mr-sm-1"
            :disabled="isExporting"
          >
            <template #button-content>
              <b-spinner
                v-if="isExporting"
                small
                class="mr-1"
              ></b-spinner>
              <feather-icon
                v-else
                size="16"
                class="mr-1"
                icon="DownloadIcon"
              />
              {{ isExporting ? '匯出中...' : '匯出' }}
            </template>
            <b-dropdown-item
              @click="exportCurrentPage"
              :disabled="isExporting"
            >
              <feather-icon size="16" class="mr-1" icon="FileTextIcon" />
              匯出此頁面
            </b-dropdown-item>
            <b-dropdown-item
              @click="exportAllRecords"
              :disabled="isExporting"
            >
              <feather-icon size="16" class="mr-1" icon="DatabaseIcon" />
              匯出全部記錄
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            class="mobile-w100"
            :to="{ name: 'worker-advance-payment-create', query: { user_id: user_id } }"
            v-if="ability.can('create', 'user_prepayments') && is_user_page"
          >
            <feather-icon class="mr-1" icon="plusIcon" size="18"></feather-icon
            >新增記錄</b-button
          >
          <b-button
            class="mobile-w100"
            :to="{ name: 'advance-payment-create' }"
            v-if="ability.can('create', 'prepayments') && !is_user_page"
          >
            <feather-icon class="mr-1" icon="plusIcon" size="18"></feather-icon
            >新增記錄</b-button
          >
        </div>
      </div>
    </b-card>
    <b-card no-body class="card-statistics bottom">
      <b-card-body class="p-2">
        <b-table
          ref="refAdvancePaymentListTable"
          class="position-relative"
          :items="fetchAdvancePayments"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="未找到匹配的記錄"
          :sort-desc.sync="isSortDirDesc"
        >
          <!-- Column: approve_status -->
          <!-- <template #cell(approve_status)="data">
            <a
              href="javascript:void(0)"
              @click="handleApproval(data.item)"
              class="d-flex align-items-center"
              :style="{ color: getApprove(data.item.approve_status_id).color }"
            >
                <feather-icon class="mr-25" icon="EditIcon" />
              {{ getApprove(data.item.approve_status_id).name }}
            </a>
          </template> -->

          <!-- Column: invoice_number -->
          <template #cell(invoice_number)="data">
            <a
              v-if="data.item.invoice && data.item.invoice.invoice_number"
              :href="`/invoice/edit/${data.item.invoice.id}`"
              target="_blank"
              class="text-primary"
            >
              {{ data.item.invoice.invoice_number }}
            </a>
            <span v-else class="text-muted">--</span>
          </template>

          <!-- Column: invoice_address -->
          <template #cell(invoice_address)="data">
            {{ data.item.invoice && data.item.invoice.address ? data.item.invoice.address : '--' }}
          </template>
          <!-- Column: receive_method -->
          <template #cell(receive_method)="data">
              {{ getReceiveMethodName(data.item.receive_method_id) }}
          </template>

          <!-- <template #cell(is_approved)="data">
            <feather-icon
              v-if="data.item.is_approved"
              icon="CheckIcon"
              size="18"
              class="mr-50 text-success"
            />
          </template> -->
          <template #cell(prepayment_status)="data">
            <a v-if="(ability.can('status', 'prepayments') && !is_user_page) || (ability.can('status', 'user_prepayments') && is_user_page)"
              href="javascript:void(0)"
              @click="handleStatus(data.item)"
              class="d-flex align-items-center"
            >
              <feather-icon class="mr-25" icon="EditIcon" />
              {{
                data.item.prepayment_status && data.item.prepayment_status.name
              }}
            </a>
            <div v-else>
              {{
                data.item.prepayment_status && data.item.prepayment_status.name
              }}
            </div>
          </template>

          <!-- Column: salary_records -->
          <template #cell(show_details)="row">
            <b-button
              v-if="row.item.transactions || row.item.salary_records || row.item.expense_items"
              size="sm"
              @click="row.toggleDetails()"
              class="mr-2"
            >
              {{ row.detailsShowing ? "隱藏" : "顯示" }}
            </b-button>
          </template>

          <template #row-details="row">
            <!-- 預支金額 (transactions) 資訊 -->
            <div class="info-container" v-if="row.item.transactions && row.item.transactions.length > 0">
              <h6 class="mb-2 text-primary">預支金額</h6>
              <b-row
                class="p-2 backgroundColor3 mb-1"
                v-for="(transaction, index) in row.item.transactions"
                :key="`transaction${index}`"
              >
                <!-- 基本資訊 -->
                <b-col cols="12" md="4">
                  <div class="row mb-50">
                    <b class="label">金額：</b>
                    <span>${{ transaction.amount }}</span>
                  </div>
                  <div class="row mb-50">
                    <b class="label">備註：</b>
                    <pre>{{ transaction.description || '--' }}</pre>
                  </div>
                  <div class="row mb-50">
                    <b class="label">接收人：</b>
                    <span>{{ getUser(transaction.user_id).name_cn }}</span>
                  </div>
                </b-col>

                <!-- 接收資訊 - 現在使用主表的收款資訊 -->
                <b-col cols="12" md="4" v-if="false">
                  <!-- <div class="row mb-50">
                    <b class="label">接收狀態：</b>
                    <span :class="item.prepayment_status_id == 4 ? 'text-success' : 'text-warning'">
                      {{ item.prepayment_status_id == 4 ? '已收款' : '未收款' }}
                    </span>
                  </div> -->
                  <div class="row mb-50">
                    <b class="label">收款方式：</b>
                    <span>{{ getReceiveMethod(item.receive_method_id).name || '--' }}</span>
                  </div>
                  <div class="row mb-50">
                    <b class="label">收款日期：</b>
                    <span>{{ item.receipt_date || '--' }}</span>
                  </div>
                  <div class="row mb-50" v-if="item.receive_method_id == 1">
                    <b class="label">支票號碼：</b>
                    <span>{{ item.check_number || '--' }}</span>
                  </div>
                </b-col>

                <!-- 批核資訊 -->
                <b-col cols="12" md="4">
                  <div class="row mb-50">
                    <b class="label">批核狀態：</b>
                    <span v-if="transaction.is_approved" class="text-success">已批核</span>
                    <span v-else-if="transaction.is_rejected" class="text-danger">已拒絕</span>
                    <span v-else class="text-warning">待批核</span>
                  </div>
                  <div class="row mb-50">
                    <b class="label">批核備註：</b>
                    <pre>{{ transaction.admin_remark || '--' }}</pre>
                  </div>
                  <div class="row mb-50">
                    <b class="label">批核日期：</b>
                    <span>{{ transaction.approve_date || '--' }}</span>
                  </div>
                </b-col>
              </b-row>
            </div>
            <!-- 工資 (salary_records) 資訊 -->
            <div class="info-container" v-if="row.item.salary_records && row.item.salary_records.length > 0">
              <h6 class="mb-2 text-success">工資</h6>
              <b-row
                class="p-2 backgroundColor5 mb-1"
                v-for="(worker, index) in row.item.salary_records"
                :key="`salary${index}`"
              >
                <!-- 基本資訊 -->
                <b-col cols="12" md="4">

                  <div class="row mb-50">
                    <b class="label">計薪方式：</b>
                    <span v-if="getSalaryDisplayType(row.item.salary_type) === 'daily'">
                      日薪
                    </span>
                    <span v-if="getSalaryDisplayType(row.item.salary_type) === 'monthly'">
                      月薪
                    </span>
                    <span v-if="getSalaryDisplayType(row.item.salary_type) === 'project'">
                      以項目工程計算
                    </span>
                  </div>

                  <div class="row mb-50">
                    <b class="label">員工：</b>
                    <span>
                      {{ getWorker(worker.user_id).position && getWorker(worker.user_id).position !== '- -' ? `[${getWorker(worker.user_id).position}]` : '' }}
                      {{ getWorker(worker.user_id).name_cn }}
                    </span>
                  </div>

                  <!-- 根據 payment_method_id 顯示不同的工資計算方式 -->
                  <!-- 日薪 (payment_method_id = 1 或 salary_type = 1) -->
                  <div class="row mb-50" v-if="getSalaryDisplayType(row.item.salary_type) === 'daily'">
                    <b class="label">預支金額：</b>
                    <span>{{ worker.working_days }}天 × ${{ worker.day_salary }}每日 = ${{ worker.total_salary }}</span>
                  </div>
                  <!-- <div class="row mb-50" v-if="getSalaryDisplayType(worker) === 'daily'">
                    <b class="label">開工日期：</b>
                    <p style="white-space: break-spaces;">{{worker.working_date && JSON.parse(worker.working_date).length > 0 && (JSON.parse(worker.working_date).join(', ') || '--') }}</p>
                  </div> -->

                  <!-- 月薪 (payment_method_id = 2 或 salary_type = 2) -->
                  <div class="row mb-50" v-if="getSalaryDisplayType(row.item.salary_type) === 'monthly'">
                    <b class="label">出糧月份：</b>
                    <span>{{ row.item.salary_year }}年 {{ row.item.salary_month }}月</span>
                  </div>
                  <div class="row mb-50" v-if="getSalaryDisplayType(row.item.salary_type) !== 'daily'">
                    <b class="label">預支金額：</b>
                    <span>${{ worker.total_salary }}</span>
                  </div>
                  <!-- <div class="row mb-50" v-if="getSalaryDisplayType(row.item.salary_type) === 'project'">
                    <b class="label">工作週期：</b>
                    <pre>{{ worker.working_date || '--' }}</pre>
                  </div> -->

                  <div class="row mb-50">
                    <b class="label">備註：</b>
                    <pre>{{ worker.remark || '--' }}</pre>
                  </div>
                </b-col>

                <!-- 接收資訊 - 現在使用主表的收款資訊 -->
                <!-- <b-col cols="12" md="4">
                  <div class="row mb-50">
                    <b class="label">接收狀態：</b>
                    <span :class="item.prepayment_status_id == 4 ? 'text-success' : 'text-warning'">
                      {{ item.prepayment_status_id == 4 ? '已收款' : '未收款' }}
                    </span>
                  </div>
                  <div class="row mb-50">
                    <b class="label">收款方式：</b>
                    <span>{{ getReceiveMethod(item.receive_method_id).name || '--' }}</span>
                  </div>
                  <div class="row mb-50" v-if="item.receive_method_id == 1">
                    <b class="label">支票號碼：</b>
                    <span>{{ item.check_number || '--' }}</span>
                  </div>
                  <div class="row mb-50">
                    <b class="label">收款日期：</b>
                    <span>{{ item.receipt_date || '--' }}</span>
                  </div>
                </b-col> -->

                <!-- 批核資訊 -->
                <b-col cols="12" md="4">
                  <div class="row mb-50">
                    <b class="label">批核狀態：</b>
                    <span v-if="worker.is_approved" class="text-success">已批核</span>
                    <span v-else-if="worker.is_rejected" class="text-danger">已拒絕</span>
                    <span v-else class="text-warning">待批核</span>
                  </div>
                  <div class="row mb-50">
                    <b class="label">批核備註：</b>
                    <pre>{{ worker.admin_remark || '--' }}</pre>
                  </div>
                  <div class="row mb-50">
                    <b class="label">批核日期：</b>
                    <span>{{ worker.approve_date || '--' }}</span>
                  </div>
                </b-col>
              </b-row>
            </div>
            <!-- <div class="info-container" v-if="row.item.expense_items">
              <b-row
                class="p-2 backgroundColor3 mb-1"
                v-for="(expense, index) in row.item.expense_items"
                :key="`cost${index}`"
              >
                <b-col cols="12">
                  <div class="row mb-50">
                    <b class="label">商品種類：</b>
                    <span>{{
                      getProductCategory(expense.product_category_id).name
                    }}</span>
                  </div>
                  <div class="row mb-50">
                    <b class="label">商品：</b>
                    <span>{{
                      getProduct(expense.product_id).product_name
                    }}</span>
                  </div>

                  <div class="row mb-50">
                    <b class="label">金額：</b>
                    <span
                      >{{ parseFloat(Number(expense.quantity).toString()) }} * ${{ expense.unit_price }} = ${{
                        expense.total
                      }}</span
                    >
                  </div>
                </b-col>
              </b-row>
            </div> -->
          </template>

          <template #cell(created_at)="data">
            {{ getDateTime(data.item.created_at) }}
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              :class="`action-trigger-btn ${
                advancePaymentsList.length <= 3 ? 'hor' : 'ver'
              }`"
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <!-- if 在用戶自己頁 -->
              <b-dropdown-item
                v-if="ability.can('view_detail', 'user_prepayments') && is_user_page"
                :to="{
                  name: 'worker-advance-payment-view',
                  params: { id: data.item.id },
                }"
              >
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">詳細資料</span>
              </b-dropdown-item>

              <!-- if 在finance頁 -->
              <b-dropdown-item
                v-if="ability.can('view_detail', 'prepayments') && !is_user_page"
                :to="{
                  name: 'advance-payment-view',
                  params: { id: data.item.id },
                }"
              >
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">詳細資料</span>
              </b-dropdown-item>

              <!-- if 在用戶自己頁 -->
              <b-dropdown-item
                 v-if="ability.can('update', 'user_prepayments') && is_user_page"
                :to="{
                  name: 'worker-advance-payment-edit',
                  params: { id: data.item.id },
                }"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">修改資料</span>
              </b-dropdown-item>

              <!-- if 在finance頁 -->
              <b-dropdown-item
                v-if="ability.can('update', 'prepayments') && !is_user_page"
                :to="{
                  name: 'advance-payment-edit',
                  params: { id: data.item.id },
                }"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">修改資料</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="(ability.can('status', 'prepayments') && !is_user_page) || (ability.can('status', 'user_prepayments') && is_user_page)"
                @click="handleStatus(data.item)"
              >
                <feather-icon icon="CheckIcon" />
                <span class="align-middle ml-50">更改預支狀態</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="(ability.can('delete', 'prepayments') && !is_user_page) || (ability.can('delete', 'user_prepayments') && is_user_page)"
                @click="removeItem(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
                <span class="align-middle ml-50">刪除記錄</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >在{{ dataMeta.of }}個記錄中，正在顯示 {{ dataMeta.from }} 至
                {{ dataMeta.to }}個記錄</span
              >
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRow"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>
    
<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { ref, onUnmounted } from "@vue/composition-api";
import useAdvancePaymentsList from "./useAdvancePaymentsList";
import advancePaymentStoreModule from "../advancePaymentStoreModule";
import ModalFilter from "../modal/ModalFilter.vue";
import ModalApproval from "../modal/ModalApproval.vue";
import ModalStatus from "../modal/ModalStatus.vue";
import { getUserData } from "@/auth/utils";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    ModalFilter,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    ModalApproval,
    ModalStatus,
    vSelect,
  },
  data() {
    return {
      approveData: null,
      prepaymentData: null,
      isExporting: false,
    };
  },
  mounted(){
    this.user_id = getUserData().id;
  },
  props: {
    routeKey: String,
  },
  methods: {
    handleStatus(item) {
      this.prepaymentData = item;
      this.$bvModal.show("modal-status");
    },
    handleApproval(item) {
      this.approveData = item;
      this.$bvModal.show("modal-approval");
    },
    removeItem(id) {
      // check number of record
      this.$swal({
        title: "你確定嗎?",
        text: "您的決定是最終決定，您將無法取回這記錄。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("advance-payment/deleteAdvancePayment", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },

    // 匯出此頁面
    exportCurrentPage() {
      if (this.isExporting) return;

      this.isExporting = true;
      this.exportRecords('current')
        .then(exportData => {
          this.downloadExcel(exportData, '預支記錄_當前頁面');
        })
        .catch(error => {
          console.error('匯出當前頁面失敗:', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "匯出失敗",
              text: "匯出當前頁面失敗，請稍後再試",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.isExporting = false;
        });
    },

    // 匯出全部記錄
    exportAllRecords() {
      if (this.isExporting) return;

      this.isExporting = true;
      this.exportRecords('all')
        .then(exportData => {
          this.downloadExcel(exportData, '預支記錄_全部記錄');
        })
        .catch(error => {
          console.error('匯出全部記錄失敗:', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "匯出失敗",
              text: "匯出全部記錄失敗，請稍後再試",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.isExporting = false;
        });
    },

    // 下載 Excel 文件
    downloadExcel(data, filename) {
      if (!data || data.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "匯出失敗",
            text: "沒有數據可以匯出",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // 計算實際記錄數量
      const actualRecordCount = data.length;

      // 匯出為 Excel (使用 ExcelJS 支援完整樣式)
      import("@/utils/excelExport").then(async (excelExport) => {
        try {
          // 使用專業級的預支記錄匯出函數
          const success = await excelExport.export_advance_payments_to_excel_styled({
            title: '預支記錄',
            data: data,
            filename: filename,
            bookType: 'xlsx'
          });

          if (success) {
            // 顯示成功訊息
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "匯出成功",
                text: `已成功匯出 ${actualRecordCount} 筆記錄，檔案包含完整的格式化樣式`,
                icon: "CheckCircleIcon",
                variant: "success",
              },
            });
          } else {
            throw new Error('匯出過程中發生錯誤');
          }
        } catch (error) {
          console.error('Excel 匯出失敗:', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "匯出失敗",
              text: "Excel 匯出過程中發生錯誤，請稍後再試",
              icon: "XCircleIcon",
              variant: "danger",
            },
          });
        }
      }).catch((error) => {
        console.error('匯出失敗:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "匯出失敗",
            text: "Excel 匯出功能載入失敗",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    },
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = "advance-payment";

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME))
      store.registerModule(
        USER_APP_STORE_MODULE_NAME,
        advancePaymentStoreModule
      );

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME))
        store.unregisterModule(USER_APP_STORE_MODULE_NAME);
    });

    const getProduct = (id) => {
      if (!id || !productOptions.value?.length) return { unit: "次" };
      return { 
        unit: "次", 
        ...productOptions.value.find(ele => ele.id === id)
      };
    };
    const getProject = (id) => {
      if (!id || !projectOptions.value?.length) return "- -";
      return projectOptions.value.find(ele => ele.id === id) || "- -";
    };
    const getReceiveMethod = (id) => {
      if (!id || !receiveMethodOptions.value?.length) return "- -";
      return receiveMethodOptions.value.find(ele => ele.id === id) || "- -";
    };
    const getUser = (id) => {
      if (!id || !userOptions.value?.length) return "- -";
      return userOptions.value.find(ele => ele.id === id) || "- -";
    };
    // const getProductCategory = (id) => {
    //   if (!id || !productCategoriesOptions.value?.length) return "- -";
    //   return productCategoriesOptions.value.find(ele => ele.id === id) || "- -";
    // };
    const getApprove = (id) => {
      if (!id || !approvalStatusOptions.value?.length) return "- -";
      return approvalStatusOptions.value.find(ele => ele.id === id) || "- -";
    };
   

    const getWorker = (workerId) => {
      if (!workerId || !userOptions.value?.length) return { name_cn: '- -', position: '- -', payment_method_id: null };
      return userOptions.value.find(ele => ele.id === workerId) || { name_cn: '- -', position: '- -', payment_method_id: null };
    }

    const {
      fetchAdvancePayments,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      advancePaymentsList,
      refAdvancePaymentListTable,
      refetchData,
      ability,
      refetchOption,
      filter_user_id,
      filter_project_id,
      filter_approval_id,
      filter_prepayment_status_id,
      projectOptions,
      productOptions,
      approvalStatusOptions,
      // productCategoriesOptions,
      userOptions,
      prepaymentStatusOptions,
      receiveMethodOptions,
      getReceiveMethodName,
      is_user_page,
      exportRecords,
    } = useAdvancePaymentsList();


    return {
      fetchAdvancePayments,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      advancePaymentsList,
      refAdvancePaymentListTable,
      refetchData,
      ability,
      refetchOption,
      filter_project_id,
      filter_user_id,
      filter_approval_id,
      filter_prepayment_status_id,
      projectOptions,
      productOptions,
      approvalStatusOptions,
      // productCategoriesOptions,
      prepaymentStatusOptions,
      receiveMethodOptions,
      userOptions,
      getProduct,
      getApprove,
      getUser,
      getReceiveMethod,
      // getProductCategory,
      getReceiveMethodName,
      is_user_page,
      getProject,
      getSalaryDisplayType: (paymentMethodId) => {
        // 根據員工的 payment_method_id 或 salary_type 決定顯示方式
        // const user = getWorker(worker.user_id);
        // const paymentMethodId = worker.salary_type;

        if (paymentMethodId === 1) {
          return 'daily'; // 日薪
        } else if (paymentMethodId === 2) {
          return 'monthly'; // 月薪
        } else if (paymentMethodId === 3) {
          return 'project'; // 項目計算
        }
      },
      getWorker,
      exportRecords,
    };
  },
};
</script>
    
<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// @media screen and (max-width: 768px) {

// }
// @media screen and (max-width: 575px) {

// }
</style>
    
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/variables/_variables.scss";
</style>
    